import React, { useState, useEffect } from 'react';
import './../styles/LoginPage.css';
import AuthService from '../services/AuthService';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import companyLogo from './../assets/images/logo-no-bg.png';
import DOMPurify from 'dompurify';
import validateInput from './../utils/validateInput';
import { useFilterContext } from '../context/FilterContext';
import { UserType } from './../constants/userType';

function LoginPage() {
  const [inputUsername, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { resetFiltersOnly } = useFilterContext();

  const {
    token,
    login,
    userType,
  } = useAuth();

  const isPoliceUser = userType === UserType.POLICE;
  const isSupportUser = userType === UserType.SUPPORT;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (token) {
      if (isPoliceUser) {
        navigate('/notifications');
      } else if (userType === UserType.SUPER) {
        navigate('/dashboard');
      } else if (isSupportUser) {
        navigate('/support-dashboard');
      } else {
        navigate('/incidents');
      }
    }
  }, [token, userType, isPoliceUser, isSupportUser, navigate]);

  useEffect(() => {
    const notice = location.state?.authNotice || sessionStorage.getItem('authNotice');
    if (notice) {
      setAuthNotice(notice);
      sessionStorage.removeItem('authNotice');
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setAuthNotice('');

    const sanitizedUsername = DOMPurify.sanitize(inputUsername);
    const sanitizedPassword = DOMPurify.sanitize(password);
    const validationError = validateInput(sanitizedUsername, sanitizedPassword);

    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      const data = await AuthService.loginUser(sanitizedUsername, sanitizedPassword);
      setIsLoading(false);

      if (data?.user) {
        const { type, groupId, username } = data.user;

        login(type, username, groupId);
        resetFiltersOnly();

        if (type === UserType.POLICE) {
          navigate('/notifications', { replace: true });
        } else if (type === UserType.SUPPORT) {
          navigate('/support-dashboard', { replace: true });
        } else if (type === UserType.SUPER) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/incidents', { replace: true });
        }
      } else if (data?.errors?.length > 0) {
        const accountLockedError = data.errors.find(
          (err) => err.message?.includes('Account locked')
        );
        if (accountLockedError) {
          setError(accountLockedError.message);
        } else {
          setError('Login failed. Check your credentials.');
        }
        setUsername('');
        setPassword('');
      } else if (data?.error) {
        setError(data.error || 'Login failed. Check your credentials.');
        setUsername('');
        setPassword('');
      } else {
        setError('Login failed. Check your credentials.');
        setUsername('');
        setPassword('');
      }
    } catch (err) {
      setIsLoading(false);
      console.error('Error during login', err);
      setError('An error occurred during login. Please try again.');
      setUsername('');
      setPassword('');
    }
  };

  return (
    <div className="login-page">
      <section className="login-shell">
        <div className="login-panel login-brand">
          <div className="login-brand-scene" aria-hidden="true">
            <span className="login-wave login-wave-top" />
            <span className="login-wave login-wave-bottom" />
          </div>

          <div className="login-brand-top">
            <img src={companyLogo} alt="Company Logo" className="login-logo" />
          </div>

          <div className="login-brand-copy">
            <h1>Security tips</h1>
            <div className="login-security-tips">
              <span>Keep credentials private.</span>
              <span>Lock your screen when away.</span>
              <span>Report unexpected access prompts.</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-panel login-card" aria-label="Login Form">
          <div className="login-header">
            <div>
              <h2>Welcome back</h2>
              <p>Enter your portal credentials to continue.</p>
            </div>
          </div>

          {error && <div className="login-alert error" role="alert">{error}</div>}
          {authNotice && <div className="login-alert warning" role="alert">{authNotice}</div>}

          <label className="login-field" htmlFor="username">
            <span>Username</span>
          <input
            id="username"
            type="text"
            placeholder="Enter your username"
            value={inputUsername}
            onChange={(e) => setUsername(e.target.value)}
            required
            aria-required="true"
            autoComplete="off"
          />
          </label>

          <label className="login-field" htmlFor="password">
            <span>Password</span>
          <input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-required="true"
            autoComplete="off"
          />
          </label>

          <div className="login-help">
            <div>
              <strong>Account reminder</strong>
              <span>Use the username assigned to your eSafe Monitor account.</span>
            </div>
            <div>
              <strong>Need access?</strong>
              <span>Contact support if your account is locked or unavailable.</span>
            </div>
          </div>

          <div className="login-actions">
            <Link to="/forgot-password" className="login-secondary">
              Forgot password?
            </Link>
            <button type="submit" className="login-primary" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Log in'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default LoginPage;
